import { Card, CardContent, CardHeader, Grid, Stack } from '@mui/material';
import { useState } from 'react';
import { useResponsive } from 'src/hooks/use-responsive';
import { RHFTextField } from 'src/components/hook-form';
import { DeepPartial, FormProvider, useForm } from 'react-hook-form';
import { RHFImagesField } from 'src/components/hook-form/rhf-images';
import { useUpsertRenovationPropertyMutation } from 'src/services/properties';
import { PropertyModel, UpsertRenovationInput } from 'src/gql/graphql';
import { omit } from 'lodash';
import { useSnackbar } from 'src/components/snackbar';
import { LoadingButton } from '@mui/lab';
import { uploadFilesToS3 } from 'src/utils/upload-s3-values';
import { BASE_URL } from 'src/services/_request';
import { SideNavigation } from '../components/side-navigation';

type Props = {
  property: DeepPartial<PropertyModel>;
};

type FormValues = UpsertRenovationInput & {
  _blobProjectDescriptionPictures: Record<string, File>;
  _blobCurrentPictures: Record<string, File>;
  _blobFuturePictures: Record<string, File>;
};

const steps = [
  'Purchase Details',
  'Total development cost',
  'Sales & Profit details',
  //   'Project Description',
  'Compare Gallery',
];

export default function PropertyRenovationView({ property }: Props) {
  const mdUp = useResponsive('up', 'md');
  const { enqueueSnackbar } = useSnackbar();

  const [activeStep, setActiveStep] = useState(0);

  const { mutateAsync } = useUpsertRenovationPropertyMutation();

  const { renovation: renovationValues } = property;

  const form = useForm<FormValues>({
    defaultValues: {
      _blobProjectDescriptionPictures: {},
      _blobCurrentPictures: {},
      _blobFuturePictures: {},

      currentPictures: renovationValues?.currentPictures || [],
      futurePictures: renovationValues?.futurePictures || [],
      purchaseDetails: {
        description: renovationValues?.purchaseDetails?.description || '',
        askingPrice: renovationValues?.purchaseDetails?.askingPrice || '',
        letterOfAttorney:
          renovationValues?.purchaseDetails?.letterOfAttorney || '',
        foreignerId: renovationValues?.purchaseDetails?.foreignerId || '',
        bankAccountRegistration:
          renovationValues?.purchaseDetails?.bankAccountRegistration || '',
        documentsTranslation:
          renovationValues?.purchaseDetails?.documentsTranslation || '',
        vat: renovationValues?.purchaseDetails?.vat || '',
        legalRegistration:
          renovationValues?.purchaseDetails?.legalRegistration || '',
      },
      developmentDetails: {
        pmCosts: renovationValues?.developmentDetails?.pmCosts || '',
        renovationCostSqm:
          renovationValues?.developmentDetails?.renovationCostSqm || '',
        otherCosts: renovationValues?.developmentDetails?.otherCosts || '',
        renovationCost:
          renovationValues?.developmentDetails?.renovationCost || '',
        totalDevelopmentCosts:
          renovationValues?.developmentDetails?.totalDevelopmentCosts || '',
        totalDevelopmentCostsPerSqm:
          renovationValues?.developmentDetails?.totalDevelopmentCostsPerSqm ||
          '',
        projectManagement:
          renovationValues?.developmentDetails?.projectManagement || '',
        description: renovationValues?.developmentDetails?.description || '',
      },
      sellDetails: {
        roe: renovationValues?.sellDetails?.roe || '',
        netRevenue: renovationValues?.sellDetails?.netRevenue || '',
        profitCost: renovationValues?.sellDetails?.profitCost || '',
        sellingCost: renovationValues?.sellDetails?.sellingCost || '',
        sellingPrice: renovationValues?.sellDetails?.sellingPrice || '',
        totalCost: renovationValues?.sellDetails?.totalCost || '',
        netProfit: renovationValues?.sellDetails?.netProfit || '',
      },
      projectDescription: {
        description: renovationValues?.projectDescription?.description || '',
        pictures: renovationValues?.projectDescription?.pictures || [],
      },
    },
  });

  const isLoading = form.formState.isSubmitting;

  const handleToStep = (step: number) => {
    if (step === null) {
      return;
    }

    setActiveStep(step);
  };

  const handleSave = form.handleSubmit(
    async (data) => {
      data.currentPictures = await uploadFilesToS3(
        `properties/${property.id}/renovation/current-pictures`,
        data._blobCurrentPictures,
        data.currentPictures
      );

      data.futurePictures = await uploadFilesToS3(
        `properties/${property.id}/renovation/future-pictures`,
        data._blobFuturePictures,
        data.futurePictures
      );

      data.projectDescription = {
        ...data.projectDescription,
        pictures: await uploadFilesToS3(
          `properties/${property.id}/renovation/project-description-pictures`,
          data._blobProjectDescriptionPictures,
          data.projectDescription.pictures ?? []
        ),
      };

      try {
        await mutateAsync({
          id: property.id!,
          data: omit(data, [
            '_blobFuturePictures',
            '_blobCurrentPictures',
            '_blobProjectDescriptionPictures',
          ]),
        });

        enqueueSnackbar('Renovation property updated', {
          variant: 'success',
        });
      } catch (error) {
        console.error('Error while updating renovation property', error);

        enqueueSnackbar('Error while updating renovation property', {
          variant: 'error',
        });
      }
    },
    (errors) => {
      console.error(errors);

      enqueueSnackbar('Error while updating renovation property', {
        variant: 'error',
      });
    }
  );

  const handleSaveAndOpen = async () => {
    await handleSave();

    // Open PDF in new tab
    window.open(
      `${BASE_URL}/property-reports/${property.id}/renovation`,
      '_blank'
    );
  };

  const handleOpen = async () => {
    // Open PDF in new tab
    window.open(
      `${BASE_URL}/property-reports/${property.id}/renovation`,
      '_blank'
    );
  };

  const renderPurchaseDetails = (
    <Card>
      <CardHeader title="Purchase Details" />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <RHFTextField
              type="number"
              name="purchaseDetails.askingPrice"
              label="Asking price, €"
              fullWidth
              helperText="For example: €1,200,000"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="purchaseDetails.letterOfAttorney"
              label="Letter of attorney, €"
              fullWidth
              helperText="For example: €100"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="purchaseDetails.foreignerId"
              label="Foreigner ID, €"
              fullWidth
              helperText="For example: €10"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="purchaseDetails.bankAccountRegistration"
              label="Bank account registration, €"
              fullWidth
              helperText="For example: €250"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="purchaseDetails.documentsTranslation"
              label="Documents translation, €"
              fullWidth
              helperText="For example: €250"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="purchaseDetails.vat"
              label="VAT (ATP), €"
              fullWidth
              helperText="For example: €105,700"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="purchaseDetails.legalRegistration"
              label="Legal, registration, €"
              fullWidth
              helperText="For example: €22,650"
            />
          </Grid>
          <Grid item xs={12}>
            <RHFTextField
              name="purchaseDetails.description"
              fullWidth
              label="Description"
              minRows={6}
              multiline
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderTotalDevelopmentCostDetails = (
    <Card>
      <CardHeader title="Total development cost" />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="developmentDetails.renovationCostSqm"
              label="Renovation cost/sqm, €"
              fullWidth
              helperText="For example: €2,234"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="developmentDetails.projectManagement"
              label="Project management, %"
              fullWidth
              helperText="For example: 20%"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="developmentDetails.renovationCost"
              label="Renovation cost, €"
              fullWidth
              helperText="For example: €96,000"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="developmentDetails.pmCosts"
              label="Other costs (PM, Financing, etc.), €"
              fullWidth
              helperText="For example: €96,000"
            />
          </Grid>
          {/* <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="developmentDetails.otherCosts"
              label="Other costs, €"
              fullWidth
              helperText="For example: €96,000"
            />
          </Grid> */}
          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="developmentDetails.totalDevelopmentCosts"
              label="Total development costs, €"
              fullWidth
              helperText="For example: €2,746,000"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="developmentDetails.totalDevelopmentCostsPerSqm"
              label="Total development cost/sqm, €"
              fullWidth
              helperText="For example: €4,250"
            />
          </Grid>

          <Grid item xs={12}>
            <RHFTextField
              name="developmentDetails.description"
              fullWidth
              label="Description"
              minRows={4}
              multiline
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderSellsProfitDetailsDetails = (
    <Card>
      <CardHeader title="Sales & Profit details" />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="sellDetails.roe"
              label="ROE, %"
              fullWidth
              helperText="For example: 20%"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="sellDetails.profitCost"
              label="Profit/Cost, %"
              fullWidth
              helperText="For example: 30%"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="sellDetails.sellingPrice"
              label="Selling price, €"
              fullWidth
              helperText="For example: €1,261,540"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="sellDetails.sellingCost"
              label="Selling cost, €"
              fullWidth
              helperText="For example: €63,077"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="sellDetails.netRevenue"
              label="Net revenue, €"
              fullWidth
              helperText="For example: €348,173"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="sellDetails.totalCost"
              label="Total cost, €"
              fullWidth
              helperText="For example: €348,173"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <RHFTextField
              type="number"
              name="sellDetails.netProfit"
              label="Net Profit, €"
              fullWidth
              helperText="For example: €42,252"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderProjectDescription = (
    <Card>
      <CardHeader title="Project Description" />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <RHFTextField
              name="projectDescription.description"
              fullWidth
              label="Description"
              minRows={4}
              multiline
            />
          </Grid>

          <Grid item xs={12}>
            <RHFImagesField
              name="projectDescription.pictures"
              uploadName="_blobProjectDescriptionPictures"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderCompareGallery = (
    <>
      <Card>
        <CardHeader title="Current Pictures" />
        <CardContent>
          <RHFImagesField
            name="currentPictures"
            uploadName="_blobCurrentPictures"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Future Inspiration" />
        <CardContent>
          <RHFImagesField
            name="futurePictures"
            uploadName="_blobFuturePictures"
          />
        </CardContent>
      </Card>
    </>
  );

  return (
    <FormProvider {...form}>
      <Stack gap={8}>
        <Stack flexDirection={mdUp ? 'row' : 'column'} gap={4}>
          <SideNavigation
            steps={steps}
            activeStep={activeStep}
            handleToStep={handleToStep}
          />

          <Stack flex={1} gap={4}>
            <Stack key={activeStep} flex={1} gap={3}>
              {activeStep === 0 && <>{renderPurchaseDetails}</>}
              {activeStep === 1 && <>{renderTotalDevelopmentCostDetails}</>}
              {activeStep === 2 && <>{renderSellsProfitDetailsDetails}</>}
              {/* {activeStep === 3 && <>{renderProjectDescription}</>} */}
              {activeStep === 3 && <>{renderCompareGallery}</>}
            </Stack>

            <Stack flexDirection="row" gap={2} justifyContent="flex-end">
              <LoadingButton
                variant="outlined"
                onClick={handleOpen}
                loading={isLoading}
              >
                Open
              </LoadingButton>

              <LoadingButton
                variant="outlined"
                onClick={handleSaveAndOpen}
                loading={isLoading}
              >
                Save & Open
              </LoadingButton>

              <LoadingButton
                variant="contained"
                onClick={handleSave}
                loading={isLoading}
              >
                Save
              </LoadingButton>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
